import { useEffect, useRef } from 'react';
import { AnnouncementService } from 'Services/announcementService';
import {
  getTypingAccessibleLabel,
  TYPING_STOPPED_ANNOUNCEMENT,
} from '../../utils/getTypingAccessibleLabel';
import { TypingPerson } from '../useTypingPersons/index.types';

const TYPING_ANNOUNCEMENT_TOPIC = 'typing-indicator';

const announce = (message: string) => {
  AnnouncementService.getInstance().announce(
    message,
    'polite',
    undefined,
    TYPING_ANNOUNCEMENT_TOPIC
  );
};

/**
 * Announces typing start/change/stop to screen readers (NFR2).
 *
 * Announces only when the aggregation label changes,
 * so repeated `typing` Pusher refreshes that keep the same label stay silent.
 * Announces {@link TYPING_STOPPED_ANNOUNCEMENT} when typers clear.
 */
export const useTypingIndicatorAnnouncement = (
  typingPersons: TypingPerson[]
): void => {
  const accessibleLabel = getTypingAccessibleLabel(
    typingPersons.map(({ contact }) => contact.name)
  );
  const previousLabelRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const previous = previousLabelRef.current;
    previousLabelRef.current = accessibleLabel;

    if (previous === undefined) {
      if (accessibleLabel) {
        announce(accessibleLabel);
      }

      return;
    }

    // `accessibleLabel` is the effect dependency, so later runs only happen
    // when the label changed. Announce the new aggregation, or that typing stopped.
    if (accessibleLabel) {
      announce(accessibleLabel);
      return;
    }

    announce(TYPING_STOPPED_ANNOUNCEMENT);
  }, [accessibleLabel]);
};
